import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useLocalePath } from "../hooks/useLocalePath";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Shield, Mail, Lock, User, ArrowRight, Loader2, Compass } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage() {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const [searchParams] = useSearchParams();
  const [isRegister, setIsRegister] = useState(searchParams.get("mode") === "register");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isRegister ? "/auth/register" : "/auth/login";
      const payload = isRegister ? form : { email: form.email, password: form.password };
      const res = await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });
      login(res.data);
      toast.success(isRegister ? "Account created!" : "Welcome back!");

      const intent = searchParams.get("intent");
      const pending = sessionStorage.getItem("pending_checkout");
      if (intent === "checkout" || pending === "premium") {
        sessionStorage.removeItem("pending_checkout");
        try {
          const checkout = await axios.post(`${API}/stripe/create-checkout-session`, {
            origin_url: window.location.origin
          }, { withCredentials: true });
          window.location.href = checkout.data.url;
          return;
        } catch {
          navigate("/pricing");
          return;
        }
      }

      const redirectTo = searchParams.get("redirect") || "/dashboard";
      navigate(redirectTo);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const intent = searchParams.get("intent");
    const redirectTo = searchParams.get("redirect") || "/dashboard";
    if (intent === "checkout") {
      sessionStorage.setItem("auth_redirect", redirectTo);
      sessionStorage.setItem("pending_checkout", "premium");
    } else {
      sessionStorage.setItem("auth_redirect", redirectTo);
    }
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-16" data-testid="auth-page">
      <Card className="w-full max-w-md border-slate-200 shadow-lg">
        <CardHeader className="text-center pb-2">
          <Link to={lp("/")} className="inline-flex items-center justify-center gap-2 mb-4">
            <Compass className="h-8 w-8 text-teal-700" />
          </Link>
          <CardTitle className="text-2xl font-bold font-heading text-slate-900" data-testid="auth-title">
            {isRegister ? t("auth.registerTitle") : t("auth.signInTitle")}
          </CardTitle>
          <CardDescription className="text-sm text-slate-500">
            {isRegister ? t("auth.registerDesc") : t("auth.signInDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <Button
            variant="outline"
            className="w-full h-12 font-medium text-sm border-slate-200 hover:bg-slate-50 mb-4"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            {t("auth.googleBtn")}
          </Button>

          <div className="flex items-center gap-3 my-4">
            <Separator className="flex-1" />
            <span className="text-xs text-slate-400 font-medium uppercase">{t("auth.orContinueWith")}</span>
            <Separator className="flex-1" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            {isRegister && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                  <User className="inline h-3.5 w-3.5 mr-1.5 text-slate-400" />
                  {t("auth.name")}
                </Label>
                <Input
                  id="name" required
                  placeholder={t("auth.namePh")}
                  className="h-11 border-slate-200"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                <Mail className="inline h-3.5 w-3.5 mr-1.5 text-slate-400" />
                {t("auth.email")}
              </Label>
              <Input
                id="email" type="email" required
                placeholder={t("auth.emailPh")}
                className="h-11 border-slate-200"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="auth-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                <Lock className="inline h-3.5 w-3.5 mr-1.5 text-slate-400" />
                {t("auth.password")}
              </Label>
              <Input
                id="password" type="password" required minLength={6}
                placeholder={t("auth.passwordPh")}
                className="h-11 border-slate-200"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="auth-password-input"
              />
            </div>
            <Button
              type="submit" disabled={loading}
              className="w-full h-11 bg-teal-700 hover:bg-teal-800 text-white font-medium"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("auth.signingIn")}</>
              ) : (
                <>{isRegister ? t("auth.registerBtn") : t("auth.signInBtn")} <ArrowRight className="ml-2 h-4 w-4" /></>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-sm text-slate-500 hover:text-slate-900 transition-colors"
              data-testid="auth-switch-mode"
            >
              {isRegister ? t("auth.switchToSignIn") : t("auth.switchToRegister")}{" "}
              <span className="text-teal-700 font-medium">
                {isRegister ? t("auth.signIn") : t("auth.register")}
              </span>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
